import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useAuth } from '@contexts/auth-provider';
import { useModules } from '@contexts/modules-provider';
import { ErrorPage } from '@shared/components/error-pages/ErrorPage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type {
  OrganizationsContextValue,
  OrganizationsProviderProps,
} from './OrganizationsProvider.types';

import { fetchOrganizations } from './api/fetch-organizations/fetchOrganizations';
import { fetchSelectedOrganization } from './api/fetch-selected-organization/fetchSelectedOrganization';
import { updateSelectedOrganization } from './api/update-selected-organization/updateSelectedOrganization';

const EMPTY_ORGANIZATIONS: OrganizationsContextValue['organizations'] = [];

const OrganizationsContext = React.createContext<
  OrganizationsContextValue | undefined
>(undefined);

const OrganizationsProvider = React.memo(
  ({ children }: Readonly<OrganizationsProviderProps>): React.ReactElement => {
    const queryClient = useQueryClient();
    const { isAuthenticated } = useAuth();
    const { modules, selectedModule } = useModules();

    const isManageModule =
      modules !== undefined && selectedModule?.name === 'Manage';

    const orgsEnabled = isAuthenticated && isManageModule;

    const {
      data: organizations = EMPTY_ORGANIZATIONS,
      isLoading: areOrganizationsLoading,
      isError: areOrganizationsError,
      error: organizationsError,
    } = useQuery({
      queryKey: ['organizations'],
      queryFn: fetchOrganizations,
      enabled: orgsEnabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

    const shouldFetchSelectedOrg = React.useMemo(
      () => orgsEnabled && organizations.length > 1,
      [orgsEnabled, organizations.length]
    );

    const {
      data: selectedOrganizationData = null,
      isLoading: isSelectedOrganizationLoading,
      isError: isSelectedOrganizationError,
      error: selectedOrganizationError,
    } = useQuery({
      queryKey: ['selectedOrganization'],
      queryFn: fetchSelectedOrganization,
      enabled: shouldFetchSelectedOrg,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

    const selectedOrganization = React.useMemo(() => {
      if (organizations.length === 1) {
        return organizations[0];
      }
      return selectedOrganizationData;
    }, [organizations, selectedOrganizationData]);

    const [isSyncingOrganization, setIsSyncingOrganization] =
      React.useState(false);

    const [isFatalError, setIsFatalError] = React.useState(false);

    const selectOrganizationMutation = useMutation({
      mutationFn: (organizationId: string) =>
        updateSelectedOrganization({ id: organizationId }),
      onMutate: async organizationId => {
        await queryClient.cancelQueries({ queryKey: ['selectedOrganization'] });

        const previousOrg = queryClient.getQueryData(['selectedOrganization']);
        const selectedOrg = organizations.find(
          org => org.id === organizationId
        );
        if (selectedOrg) {
          queryClient.setQueryData(['selectedOrganization'], selectedOrg);
        }
        return { previousOrg };
      },
      onError: (_error, _organizationId, context) => {
        if (context?.previousOrg) {
          queryClient.setQueryData(
            ['selectedOrganization'],
            context.previousOrg
          );
          emitFlagMessage(
            'danger',
            'Failed to switch organization. Please try again.',
            5000
          );
        } else {
          setIsFatalError(true);
        }
      },
    });

    const selectOrganizationMutationRef = React.useRef(
      selectOrganizationMutation
    );
    selectOrganizationMutationRef.current = selectOrganizationMutation;

    const selectedOrganizationRef = React.useRef(selectedOrganization);
    selectedOrganizationRef.current = selectedOrganization;

    const selectOrganization = React.useCallback(
      async (organizationId: string): Promise<void> => {
        const mutation = selectOrganizationMutationRef.current;
        const currentOrg = selectedOrganizationRef.current;
        if (mutation.isPending) {
          return;
        }
        if (currentOrg?.id === organizationId) {
          return;
        }
        await mutation.mutateAsync(organizationId);
      },
      []
    );

    const syncOrganizationFromUrl = React.useCallback(
      async (organizationId: string): Promise<void> => {
        const targetOrg = organizations.find(org => org.id === organizationId);
        if (!targetOrg) {
          return;
        }

        const cachedPreviousOrg = queryClient.getQueryData([
          'selectedOrganization',
        ]);

        setIsSyncingOrganization(true);

        queryClient.setQueryData(['selectedOrganization'], targetOrg);

        try {
          await updateSelectedOrganization({ id: organizationId });
        } catch (error) {
          queryClient.setQueryData(
            ['selectedOrganization'],
            cachedPreviousOrg ?? null
          );
          throw error;
        } finally {
          setIsSyncingOrganization(false);
        }
      },
      [organizations, queryClient]
    );

    const hasMultipleOrganizations = React.useMemo(
      () => organizations.length > 1,
      [organizations]
    );

    const combinedIsLoading = React.useMemo(
      () =>
        areOrganizationsLoading ||
        (shouldFetchSelectedOrg && isSelectedOrganizationLoading),
      [
        areOrganizationsLoading,
        shouldFetchSelectedOrg,
        isSelectedOrganizationLoading,
      ]
    );

    const contextValue: OrganizationsContextValue = React.useMemo(
      () => ({
        organizations,
        selectedOrganization,
        hasMultipleOrganizations,
        isOrganizationsLoading: combinedIsLoading,
        isOrganizationsError:
          isSelectedOrganizationError || areOrganizationsError,
        organizationsError: selectedOrganizationError || organizationsError,
        selectOrganization,
        syncOrganizationFromUrl,
        isSelecting: selectOrganizationMutation.isPending,
        isSyncingOrganization,
      }),
      [
        organizations,
        selectedOrganization,
        hasMultipleOrganizations,
        combinedIsLoading,
        isSelectedOrganizationError,
        areOrganizationsError,
        selectedOrganizationError,
        organizationsError,
        selectOrganization,
        syncOrganizationFromUrl,
        selectOrganizationMutation.isPending,
        isSyncingOrganization,
      ]
    );

    const hasInitializationError =
      areOrganizationsError || isSelectedOrganizationError || isFatalError;

    return (
      <OrganizationsContext value={contextValue}>
        {hasInitializationError ? (
          <ErrorPage type="internal-server-error" />
        ) : (
          children
        )}
      </OrganizationsContext>
    );
  }
);

OrganizationsProvider.displayName = 'OrganizationsProvider';

export { OrganizationsContext, OrganizationsProvider };
